import {
  faArrowsRepeat,
  faCogs,
  faCreditCard,
  faGauge,
  faGaugeHigh,
  faPlug,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import VoxelMenuItemContainer from '../../../components/voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelPopoverMenuItem from '../../../components/voxel/PopoverMenu/VoxelPopoverMenuItem';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';
import VoxelDivider from '../../../components/voxel/VoxelDivider';
import { AppTierTypeGq } from '../../../graphql/projects/models/AppTierTypeGq';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { PublicUserGq } from '../../../graphql/users/models/PublicUserGq';
import { useActiveTabContext } from '../../../logic/activeTabContext/useActiveTabContext';
import { TeamMenuChoices } from '../../TeamPage/TeamPage';
import SwitchTeamDialog from './SwitchTeamDialog';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import VoxelMenuDivider from '../../../components/voxel/PopoverMenu/VoxelMenuDivider';

export interface StartPageTeamButtonProps {
  team: CurrentTeamGq;
  availableUsers: PublicUserGq[];
  currentUser: CurrentUserGq;
  appUsageBlocked?: boolean;
}

export const StartPageTeamButton: React.FC<StartPageTeamButtonProps> = ({
  team,
  availableUsers,
  currentUser,
  appUsageBlocked,
}) => {
  const history = useHistory();

  const alternativeUsers = useMemo(
    () =>
      chain(availableUsers)
        .filter((au) => au.team.id !== team.id)
        .orderBy((au) => au.team.name)
        .value(),
    [team, availableUsers]
  );

  // Update selected team if needed
  const activeTabContext = useActiveTabContext();
  useEffect(() => {
    if (!currentUser?.team?.id) return;
    const currentTeam = activeTabContext.currentTeamId;
    if (currentTeam) return;
    activeTabContext.setCurrentTeamId(currentUser.team.id, true);
  }, [currentUser]);

  const xPadding = 'px-4';
  const leftWidth = 'w-20';
  const [openSwitchTeam, setOpenSwitchTeam] = useState(false);

  const [open, setOpen] = useState(false);

  return (
    <>
      <VoxelPopover enableArrow placement="bottom" open={open} onOpenChange={setOpen}>
        <VoxelPopoverTrigger>
          <button
            className={`
          h-full  px-2
          flex flex-row flex-nowrap items-center gap-2
          bg-white hover:bg-voxel-gray-50
           `}
          >
            {
              <VoxelAvatar
                name={team.name}
                size="md"
                error={
                  appUsageBlocked || currentUser?.team?.appTier?.tierType === AppTierTypeGq.NONE
                }
              />
            }
            <VoxelText variant="appbar" color={open ? 'medium' : 'slight'} noWrap>
              {team.name}
            </VoxelText>
          </button>
        </VoxelPopoverTrigger>
        <VoxelPopoverContent>
          <div className="w-72 flex flex-col py-1">
            {/* Current team */}
            <div
              className={`${xPadding} flex flex-col gap-4 justify-center items-center flex-nowrap p-6 !pb-4`}
            >
              <VoxelText variant={'bread-medium'} className="text-center">
                Team account
              </VoxelText>
              <div className={`${leftWidth} flex flex-row justify-center items-center`}>
                <VoxelAvatar name={team?.name} size={'xl'} />
              </div>
              <VoxelText variant={'title3'} className="text-center">
                {team?.name}
              </VoxelText>
            </div>
            <VoxelMenuItemContainer size="md">
              <VoxelMenuDivider />
              <VoxelPopoverMenuItem
                onClick={() => history.push('/team/' + TeamMenuChoices.Settings)}
                title={'Settings'}
                icon={<FontAwesomeIcon icon={faCogs} />}
              />
              <VoxelPopoverMenuItem
                onClick={() => history.push('/team/' + TeamMenuChoices.Members)}
                title={'Members'}
                icon={<FontAwesomeIcon icon={faUsers} />}
              />
              <VoxelPopoverMenuItem
                onClick={() => history.push('/team/' + TeamMenuChoices.Connections)}
                title={'Team Connections'}
                icon={<FontAwesomeIcon icon={faPlug} />}
              />
              <VoxelPopoverMenuItem
                onClick={() => history.push('/team/' + TeamMenuChoices.Usage)}
                title={'Usage'}
                icon={
                  <FontAwesomeIcon
                    icon={appUsageBlocked ? faGaugeHigh : faGauge}
                    className={`${appUsageBlocked ? 'text-error-700' : ''}`}
                  />
                }
                error={appUsageBlocked}
              />
              <VoxelPopoverMenuItem
                onClick={() => history.push('/team/' + TeamMenuChoices.Subscription)}
                title={'Subscription'}
                icon={
                  <FontAwesomeIcon
                    icon={faCreditCard}
                    className={`${currentUser?.team?.appTier?.tierType === AppTierTypeGq.NONE
                      ? 'text-error-700'
                      : ''
                      }`}
                  />
                }
                error={currentUser?.team?.appTier?.tierType === AppTierTypeGq.NONE}
              />
              {!!alternativeUsers.length && (
                <>
                  <VoxelMenuDivider />
                  <VoxelPopoverMenuItem
                    onClick={() => setOpenSwitchTeam(true)}
                    title={'Switch Team'}
                    icon={<FontAwesomeIcon icon={faArrowsRepeat} />}
                  />
                </>
              )}
            </VoxelMenuItemContainer>
          </div>
        </VoxelPopoverContent>
      </VoxelPopover>

      <SwitchTeamDialog
        team={team}
        availableUsers={availableUsers}
        open={openSwitchTeam}
        setOpen={setOpenSwitchTeam}
      />
    </>
  );
};

export default StartPageTeamButton;
